// Cookieless first-party visit counter for /admin/analytics.
//
// Unlike GA4 / Clarity / Meta (src/lib/tracking.ts), it needs no cookie consent, so it
// runs for everyone and doesn't wait for the banner. What it keeps in the browser:
// - always: this tab's visit (sessionStorage, gone when the tab closes), so a reload or a
//   trip to WhatsApp and back is the same visit, not a new one;
// - only with analytics consent: the first non-direct arrival (where they came from and
//   the ad click id) for 90 days in localStorage, so a booking made days after an ad
//   click is still credited to the ad;
// - on our own devices: the ?internal=<token> mark and a random id for that device (localStorage).
// The `track` edge function turns IP + user agent into a hash that rotates daily and never
// keeps the IP. It never reads what visitors type into forms.

import { hasPerfFlag } from "./perf-flags";
import { CONSENT_EVENT, readConsent } from "./tracking";

const ENDPOINT = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/track`;
const FLUSH_EVERY_MS = 10_000;
const NAV_CLICK_WINDOW_MS = 2_000; // a click followed by a page change within this = the click caused it
const MAX_TIME_DELTA_MS = 15 * 60_000;
const MAX_BATCH = 50;
const VISIT_IDLE_MS = 30 * 60_000; // a tab idle this long starts a new visit
const FIRST_TOUCH_MS = 90 * 86_400_000;
const VISIT_KEY = "ulture_visit"; // sessionStorage
const FIRST_TOUCH_KEY = "ulture_ft"; // localStorage, analytics consent only
const INTERNAL_KEY = "ulture_internal"; // localStorage, our own devices
const INTERNAL_DEVICE_KEY = "ulture_internal_device"; // localStorage: a marked device's own id, for the panel's device count
const CLICK_ID_KEYS = ["gclid", "gbraid", "wbraid", "fbclid", "msclkid", "ttclid"] as const;
const CAMPAIGN_KEYS = ["utm_source", "utm_medium", "utm_campaign", ...CLICK_ID_KEYS];

type SiteEvent = {
  type: "view" | "time" | "click" | "out" | "nav" | "booking" | "video";
  path?: string | null;
  label?: string;
  target?: string;
  ms?: number;
  props?: Record<string, unknown>;
};

/** What a visit arrived with: the landing URL's campaign fields and the referrer. */
type Touch = {
  landing: string;
  ref?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  s?: string;
  app?: string;
  ids: Partial<Record<(typeof CLICK_ID_KEYS)[number], string>>;
  ts?: number;
};

type Consent = { analytics: boolean; marketing: boolean } | null;

export type BookingStep = "date" | "time" | "submit" | "success" | "error";
export type VideoStep = "seen" | "tap" | "play" | "watch";

let sid = "";
let visitStartedAt = 0;
let visitTouch: Touch | null = null;
let started = false;
let disabled = false;
let meta: Record<string, unknown> = {};
const queue: SiteEvent[] = [];
let currentPath: string | null = null;
let visibleSince: number | null = null;
let lastClick: { label: string; at: number } | null = null;
let lastVisitSave = 0;

function makeId() {
  try {
    return crypto.randomUUID();
  } catch {
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
  }
}

// Storage can throw (private mode, blocked site data): analytics must never break the page.
function safe<T>(fn: () => T, fallback: T): T {
  try {
    return fn();
  } catch {
    return fallback;
  }
}

const isAdminPath = (path: string | null) => !!path && path.startsWith("/admin");

// Browsers logged into /admin are our own team: their visits are marked internal
// and left out of the panel.
function isInternalBrowser() {
  return safe(() => Object.keys(localStorage).some((k) => /^sb-.+-auth-token$/.test(k)), false);
}

// ulture.co/?internal=<token> marks this device as ours for good (?internal=off undoes it).
// The token is checked by the server, so it isn't in the site's code; it's taken out of
// the address bar right away so it doesn't get shared.
function internalToken(): string | undefined {
  const params = new URLSearchParams(window.location.search);
  const value = params.get("internal");
  if (value !== null) {
    safe(() => {
      if (value === "off" || !value) {
        localStorage.removeItem(INTERNAL_KEY);
        localStorage.removeItem(INTERNAL_DEVICE_KEY);
      } else {
        localStorage.setItem(INTERNAL_KEY, value.slice(0, 100));
      }
    }, undefined);
    params.delete("internal");
    const q = params.toString();
    safe(() => window.history.replaceState(window.history.state, "", `${window.location.pathname}${q ? `?${q}` : ""}${window.location.hash}`), undefined);
  }
  return safe(() => localStorage.getItem(INTERNAL_KEY) || undefined, undefined);
}

// The marked device's id (made the first time it's needed, also on devices marked earlier).
function internalDevice(token: string | undefined): string | undefined {
  if (!token) return undefined;
  return safe(() => {
    let id = localStorage.getItem(INTERNAL_DEVICE_KEY);
    if (!id) {
      id = makeId();
      localStorage.setItem(INTERNAL_DEVICE_KEY, id);
    }
    return id;
  }, undefined);
}

function inAppBrowser() {
  const ua = navigator.userAgent;
  return /Instagram/i.test(ua) ? "Instagram"
    : /FBAN|FBAV|FB_IAB/i.test(ua) ? "Facebook"
    : /musical_ly|BytedanceWebview|TikTok/i.test(ua) ? "TikTok"
    : /LinkedInApp/i.test(ua) ? "LinkedIn"
    : undefined;
}

function landingTouch(): Touch {
  const params = new URLSearchParams(window.location.search);
  const ids: Touch["ids"] = {};
  for (const k of CLICK_ID_KEYS) {
    const v = params.get(k);
    if (v) ids[k] = v.slice(0, 512);
  }
  const get = (k: string) => params.get(k)?.slice(0, 150) || undefined;
  return {
    landing: window.location.pathname,
    ref: document.referrer || undefined,
    utm_source: get("utm_source"),
    utm_medium: get("utm_medium"),
    utm_campaign: get("utm_campaign"),
    utm_term: get("utm_term"),
    utm_content: get("utm_content"),
    s: get("s"),
    app: inAppBrowser(),
    ids,
  };
}

const refHost = (ref?: string) => safe(() => (ref ? new URL(ref).hostname : ""), "");

/** Came from somewhere (an ad, a tagged link, another site, an app), not typed in. */
function isCampaignTouch(t: Touch) {
  const host = refHost(t.ref);
  return !!(t.utm_source || Object.keys(t.ids).length || t.app || (host && !/(^|\.)ulture\.co$/.test(host)));
}

// This tab's visit: kept on reload and when coming back from another site, unless the tab
// sat idle for 30 minutes or the visitor came back through a different ad or tagged link.
function resumeVisit() {
  const now = Date.now();
  const params = new URLSearchParams(window.location.search);
  const sig = CAMPAIGN_KEYS.map((k) => params.get(k) ?? "").join("|");
  const hasCampaign = sig.replace(/\|/g, "") !== "";
  const saved = safe(() => JSON.parse(sessionStorage.getItem(VISIT_KEY) || "null"), null) as
    | { id?: unknown; at?: number; start?: number; sig?: string; touch?: Touch }
    | null;
  const resume =
    saved && typeof saved.id === "string" && typeof saved.at === "number" && now - saved.at < VISIT_IDLE_MS &&
    (!hasCampaign || saved.sig === sig);
  if (resume) {
    sid = saved!.id as string;
    visitStartedAt = saved!.start ?? saved!.at!;
    visitTouch = saved!.touch ?? landingTouch();
  } else {
    sid = makeId();
    visitStartedAt = now;
    visitTouch = landingTouch();
  }
  saveVisit(hasCampaign ? sig : resume ? saved!.sig ?? "" : "");
}

let visitSig = "";
function saveVisit(sig = visitSig) {
  visitSig = sig;
  lastVisitSave = Date.now();
  safe(() => sessionStorage.setItem(VISIT_KEY, JSON.stringify({ id: sid, at: lastVisitSave, start: visitStartedAt, sig, touch: visitTouch })), undefined);
}

function consentNow(): Consent {
  const c = readConsent();
  return c ? { analytics: !!c.analytics, marketing: !!c.marketing } : null;
}

// The first non-direct arrival, kept 90 days, only while analytics cookies are allowed.
function firstTouch(consent: Consent): Touch | undefined {
  if (!consent?.analytics) {
    safe(() => localStorage.removeItem(FIRST_TOUCH_KEY), undefined);
    return undefined;
  }
  const now = Date.now();
  let ft = safe(() => JSON.parse(localStorage.getItem(FIRST_TOUCH_KEY) || "null"), null) as Touch | null;
  if (ft && !(typeof ft.ts === "number" && now - ft.ts < FIRST_TOUCH_MS)) ft = null;
  if (!ft && visitTouch && isCampaignTouch(visitTouch)) {
    const host = refHost(visitTouch.ref);
    ft = { ...visitTouch, ref: host ? `https://${host}/` : undefined, ts: visitStartedAt };
    safe(() => localStorage.setItem(FIRST_TOUCH_KEY, JSON.stringify(ft)), undefined);
  }
  return ft ?? undefined;
}

function refreshConsent() {
  const consent = consentNow();
  meta.consent = consent;
  const ft = firstTouch(consent);
  if (ft) meta.ft = ft;
  else delete meta.ft;
}

function buildMeta(): Record<string, unknown> {
  let tz: string | undefined;
  try {
    tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    tz = undefined;
  }
  const token = internalToken();
  return {
    ...visitTouch,
    lang: navigator.language,
    tz,
    internal: isInternalBrowser(),
    internal_token: token,
    internal_device: internalDevice(token),
  };
}

function push(ev: SiteEvent, flushNow = false) {
  if (disabled) return;
  queue.push(ev);
  if (Date.now() - lastVisitSave > 30_000) saveVisit();
  if (flushNow || queue.length >= MAX_BATCH) flush(false);
}

function flush(useBeacon: boolean) {
  if (!queue.length) return;
  const events = queue.splice(0, MAX_BATCH);
  const payload = JSON.stringify({ sid, meta, events });
  saveVisit();
  try {
    if (useBeacon && navigator.sendBeacon?.(ENDPOINT, new Blob([payload], { type: "text/plain" }))) return;
    void fetch(ENDPOINT, {
      method: "POST",
      body: payload,
      headers: { "Content-Type": "text/plain" },
      keepalive: true,
      mode: "no-cors",
    }).catch(() => {});
  } catch {
    // Analytics must never break the page.
  }
}

// Report the visible time spent on the current page since the last report.
function reportTime() {
  if (!currentPath || isAdminPath(currentPath) || visibleSince === null) return;
  const now = Date.now();
  const ms = Math.min(now - visibleSince, MAX_TIME_DELTA_MS);
  visibleSince = document.visibilityState === "visible" ? now : null;
  if (ms >= 500) push({ type: "time", path: currentPath, ms });
}

function onVisibilityChange() {
  if (document.visibilityState === "hidden") {
    reportTime();
    visibleSince = null;
    flush(true);
  } else {
    visibleSince = Date.now();
  }
}

function onClick(e: MouseEvent) {
  if (isAdminPath(currentPath)) return;
  const target = e.target as Element | null;
  const el = target?.closest?.("a, button, [role='button'], [data-track]");
  if (!el || el.closest("[data-track-ignore]")) return;

  const label = (el.getAttribute("data-track") || el.getAttribute("aria-label") || el.textContent || "")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 80);
  const href = el instanceof HTMLAnchorElement ? el.href : "";
  let external = false;
  let internalPath: string | undefined;
  if (href) {
    try {
      const url = new URL(href, window.location.href);
      external = url.origin !== window.location.origin;
      internalPath = external ? undefined : url.pathname;
    } catch {
      // ignore malformed hrefs
    }
  }

  push({ type: external ? "out" : "click", path: currentPath, label, target: external ? href : internalPath });
  lastClick = { label, at: Date.now() };
}

export function startSiteAnalytics() {
  if (started || typeof window === "undefined") return;
  started = true;
  // Only the real site counts: not the dev server, the /ev preview (localhost) or Lovable's previews.
  const liveHost = /^(www\.)?ulture\.co$/.test(window.location.hostname);
  if (!liveHost || hasPerfFlag("notrack") || navigator.webdriver) {
    disabled = true;
    return;
  }
  resumeVisit();
  meta = buildMeta();
  refreshConsent();
  if (document.visibilityState === "visible") visibleSince = Date.now();
  document.addEventListener("click", onClick, true);
  document.addEventListener("visibilitychange", onVisibilityChange);
  window.addEventListener(CONSENT_EVENT, refreshConsent);
  window.addEventListener("pagehide", () => {
    reportTime();
    flush(true);
  });
  window.setInterval(() => flush(false), FLUSH_EVERY_MS);
}

/** Call on every route change with the pathname (no query string). */
export function sitePageview(path: string) {
  startSiteAnalytics();
  if (disabled || path === currentPath) return;

  reportTime();
  const from = currentPath;
  currentPath = path;
  visibleSince = document.visibilityState === "visible" ? Date.now() : null;
  if (isAdminPath(path)) return;

  // /booking?src=<page>-<section>: which button opened the calendar.
  const src = /^\/booking\/?$/.test(path) ? new URLSearchParams(window.location.search).get("src")?.slice(0, 80) : undefined;
  // The first page of each page load goes out at once: someone who leaves within seconds
  // still counts, without relying on the browser sending anything as the page closes
  // (in-app browsers often don't).
  push({ type: "view", path, ...(src ? { props: { src } } : {}) }, from === null);
  if (from && lastClick && Date.now() - lastClick.at < NAV_CLICK_WINDOW_MS) {
    push({ type: "nav", path: from, label: lastClick.label, target: path });
  }
  lastClick = null;
}

/** Steps inside the booking calendar. Never pass what the visitor typed. */
export function trackBookingStep(step: BookingStep, props?: Record<string, unknown>) {
  push({ type: "booking", path: currentPath, label: step, props }, step !== "date" && step !== "time");
}

/**
 * The demo video on the phone home (DemoVideo.jsx): the block seen, a tap, the real start, and
 * `watch` = milliseconds actually played since the last report, with the furthest point (pct).
 * Everything but `seen` goes out at once, so someone who closes the page right after still counts.
 */
export function trackVideo(step: VideoStep, ms?: number, props?: Record<string, unknown>) {
  if (!sid) return;
  push({ type: "video", path: currentPath, label: step, ...(ms != null ? { ms } : {}), ...(props ? { props } : {}) }, step !== "seen");
}

/**
 * This visit, for create-booking: the booking keeps the visit's source (and its first
 * touch and ad click id) even if the visit hasn't reached the database yet.
 */
export function visitSnapshot() {
  if (disabled || !sid) return null;
  refreshConsent();
  return {
    sid,
    meta,
    consent: meta.consent ?? null,
    src: new URLSearchParams(window.location.search).get("src")?.slice(0, 80) || undefined,
  };
}
